import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PlusIcon, EyeIcon, CurrencyDollarIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, BellIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { feeAPI, studentAPI, batchAPI, enrollmentAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';
import { authService } from '../../services/auth';
import FeeReminderForm from './FeeReminderForm';

const PAGE_SIZE = 20;

const Fees = () => {
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [selectedFeeStructure, setSelectedFeeStructure] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentAPI.getAll(0, 500, true).then(res => res.data),
  });

  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => batchAPI.getAll(0, 500, true).then(res => res.data),
  });

  const { data: enrollments, isLoading: enrollmentsLoading } = useQuery({
    queryKey: ['enrollments'],
    queryFn: () => enrollmentAPI.getAll(0, 500, true).then(res => res.data),
  });

  // Server-side pagination and search for fee structures
  const { data: feeStructuresData, isLoading: feesLoading, isFetching } = useQuery({
    queryKey: ['feeStructures', selectedStudent, currentPage, debouncedSearchTerm],
    queryFn: async () => {
      if (selectedStudent) {
        // When filtering by student, get all their fee structures (usually small number)
        return await feeAPI.getByStudent(selectedStudent).then(res => res.data);
      } else {
        // Use server-side pagination and search
        const skip = currentPage * PAGE_SIZE;
        const search = debouncedSearchTerm || null;
        return await feeAPI.getAll(skip, PAGE_SIZE, true, search).then(res => res.data);
      }
    },
    keepPreviousData: true, // Keep previous data while fetching new page
  });

  const feeStructures = useMemo(() => feeStructuresData || [], [feeStructuresData]);
  const hasMorePages = feeStructures.length === PAGE_SIZE;

  // Reset to first page when filters change
  const handleStudentChange = (value) => {
    setSelectedStudent(value);
    setCurrentPage(0);
  };

  const isLoading = studentsLoading || batchesLoading || enrollmentsLoading || feesLoading;
  const isPageLoading = isFetching && !feesLoading;

  // Memoized lookup maps for O(1) access
  const studentMap = useMemo(() => {
    if (!students) return {};
    return students.reduce((acc, s) => {
      acc[s._id || s.id] = s;
      return acc;
    }, {});
  }, [students]);

  const batchMap = useMemo(() => {
    if (!batches) return {};
    return batches.reduce((acc, b) => {
      acc[b._id || b.id] = b;
      return acc;
    }, {});
  }, [batches]);

  const enrollmentMap = useMemo(() => {
    if (!enrollments) return {};
    return enrollments.reduce((acc, e) => {
      const studentId = e.student_id || e.student?.id;
      const batchId = e.batch_id || e.batch?.id;
      if (studentId && batchId) {
        acc[`${studentId}_${batchId}`] = e;
      }
      return acc;
    }, {});
  }, [enrollments]);

  const getStudentName = useCallback((studentId) => {
    const student = studentMap[studentId];
    return student ? `${student.first_name} ${student.last_name}` : 'Unknown Student';
  }, [studentMap]);

  const getBatchName = useCallback((batchId) => {
    const batch = batchMap[batchId];
    return batch ? batch.batch_name : 'Batch Deleted';
  }, [batchMap]);

  const getPaymentStatus = useCallback((feeStructure) => {
    // Use backend-provided total_paid which already includes enrollment fees
    let totalPaid;
    if (feeStructure.total_paid !== undefined) {
      totalPaid = feeStructure.total_paid;
    } else {
      // Fallback: Calculate manually if backend doesn't provide total_paid
      // Get enrollment fee payment for this student-batch combination using O(1) lookup
      const enrollmentKey = `${feeStructure.student_id}_${feeStructure.batch_id}`;
      const enrollment = enrollmentMap[enrollmentKey];

      // Calculate enrollment fee paid
      const enrollmentFeePaid = enrollment?.enrollment_fee_paid ? (enrollment.enrollment_fee_amount || 0) : 0;

      // Calculate regular payments (excluding enrollment fee payments)
      const regularPayments = feeStructure.payments?.reduce((sum, payment) => {
        // Skip enrollment fee payments as they're handled separately
        if (payment.remarks && payment.remarks.toLowerCase().includes('enrollment fee')) {
          return sum;
        }
        return sum + payment.amount;
      }, 0) || 0;

      // Total paid includes both enrollment fee and regular payments
      totalPaid = enrollmentFeePaid + regularPayments;
    }

    const outstanding = feeStructure.final_amount - totalPaid;

    if (outstanding <= 0) return { status: 'Fully Paid', color: 'bg-green-900/30 text-green-300 border border-green-700' };
    if (totalPaid > 0) return { status: 'Partial', color: 'bg-yellow-900/30 text-yellow-300 border border-yellow-700' };
    return { status: 'Pending', color: 'bg-red-900/30 text-red-300 border border-red-700' };
  }, [enrollmentMap]);

  const getTotalPaid = useCallback((feeStructure) => {
    // Use backend-provided total_paid which already includes enrollment fees
    if (feeStructure.total_paid !== undefined) {
      return feeStructure.total_paid;
    }

    // Fallback: Calculate manually if backend doesn't provide total_paid
    // Get enrollment fee payment for this student-batch combination using O(1) lookup
    const enrollmentKey = `${feeStructure.student_id}_${feeStructure.batch_id}`;
    const enrollment = enrollmentMap[enrollmentKey];

    // Calculate enrollment fee paid
    const enrollmentFeePaid = enrollment?.enrollment_fee_paid ? (enrollment.enrollment_fee_amount || 0) : 0;

    // Calculate regular payments (excluding enrollment fee payments)
    const regularPayments = feeStructure.payments?.reduce((sum, payment) => {
      // Skip enrollment fee payments as they're handled separately
      if (payment.remarks && payment.remarks.toLowerCase().includes('enrollment fee')) {
        return sum;
      }
      return sum + payment.amount;
    }, 0) || 0;

    // Total paid includes both enrollment fee and regular payments
    return enrollmentFeePaid + regularPayments;
  }, [enrollmentMap]);

  // Filter fee structures based on batch and payment status (search is now server-side)
  const filteredFeeStructures = useMemo(() => {
    if (!feeStructures) return [];

    return feeStructures.filter(feeStructure => {
      const matchesBatch = !selectedBatch ||
        (feeStructure.batch_id === selectedBatch ||
         feeStructure.batch?.id === selectedBatch ||
         feeStructure.batch?.ref?.id === selectedBatch);

      // Filter by payment status
      const paymentStatus = getPaymentStatus(feeStructure);
      const matchesPaymentStatus = !selectedPaymentStatus || paymentStatus.status === selectedPaymentStatus;

      return matchesBatch && matchesPaymentStatus;
    });
  }, [feeStructures, selectedBatch, selectedPaymentStatus, getPaymentStatus]);

  // Calculate summary statistics (memoized)
  const summaryStats = useMemo(() => {
    if (!filteredFeeStructures || filteredFeeStructures.length === 0) {
      return {
        totalFeeAmount: 0,
        totalPaidAmount: 0,
        totalPendingAmount: 0,
        pendingCount: 0
      };
    }

    let totalFeeAmount = 0;
    let totalPaidAmount = 0;
    let totalPendingAmount = 0;
    let pendingCount = 0;

    filteredFeeStructures.forEach(feeStructure => {
      const totalPaid = getTotalPaid(feeStructure);
      const outstanding = feeStructure.final_amount - totalPaid;

      totalFeeAmount += feeStructure.final_amount;
      totalPaidAmount += totalPaid;

      if (outstanding > 0) {
        totalPendingAmount += outstanding;
        pendingCount++;
      }
    });

    return {
      totalFeeAmount,
      totalPaidAmount,
      totalPendingAmount,
      pendingCount
    };
  }, [filteredFeeStructures, getTotalPaid]);
  
  // Get current user role for conditional rendering
  const user = authService.getUser();
  const userRole = user?.role;
  const canViewSummaryStats = userRole === 'admin';
  const canSendReminders = userRole === 'admin' || userRole === 'manager' || userRole === 'accountant';

  const handleSendReminder = (feeStructure) => {
    setSelectedFeeStructure(feeStructure);
    setShowReminderForm(true);
  };

  const handleReminderSuccess = () => {
    setShowReminderForm(false);
    setSelectedFeeStructure(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Fee Structures</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">Manage student fee structures and payments</p>
        </div>
        <Link
          to="/fees/new"
          className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 focus:ring-offset-gray-800"
        >
          <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">New Fee Structure</span>
          <span className="sm:hidden">New Fee</span>
        </Link>
      </div>

      {/* Summary Statistics - Only visible to ADMIN */}
      {canViewSummaryStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg bg-blue-500">
                <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Total Fee Amount</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{formatIndianCurrency(summaryStats.totalFeeAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg bg-green-500">
                <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Total Paid Amount</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{formatIndianCurrency(summaryStats.totalPaidAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg bg-yellow-500">
                <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Pending Fees Amount</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{formatIndianCurrency(summaryStats.totalPendingAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg bg-red-500">
                <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Pending Fee Structures</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{summaryStats.pendingCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Filter by Student</label>
            <select
              value={selectedStudent}
              onChange={(e) => handleStudentChange(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Students</option>
              {students?.map((student) => (
                <option key={student._id || student.id} value={student._id || student.id}>
                  {student.first_name} {student.last_name} ({student.student_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Filter by Batch</label>
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Batches</option>
              {batches?.map((batch) => (
                <option key={batch._id || batch.id} value={batch._id || batch.id}>
                  {batch.batch_name} - {batch.subject}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Filter by Payment Status</label>
            <select
              value={selectedPaymentStatus}
              onChange={(e) => setSelectedPaymentStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Statuses</option>
              <option value="Fully Paid">Fully Paid</option>
              <option value="Partial">Partial</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-500 focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 text-sm sm:text-base"
                placeholder="Search fee structures..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fee Structures List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg font-semibold text-white">
              {filteredFeeStructures.length} Fee Structure{filteredFeeStructures.length !== 1 ? 's' : ''}
            </h2>
            {canViewSummaryStats && summaryStats.pendingCount > 0 && (
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
                <span className="text-xs sm:text-sm text-yellow-400">
                  {summaryStats.pendingCount} pending • {formatIndianCurrency(summaryStats.totalPendingAmount)} outstanding
                </span>
              </div>
            )}
          </div>
        </div>
        
        {filteredFeeStructures.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <CurrencyDollarIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-300">No fee structures found</h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-400">
              {selectedStudent || selectedBatch || selectedPaymentStatus || searchTerm ? 'Try adjusting your filters.' : 'Get started by creating a new fee structure.'}
            </p>
            {!selectedStudent && !selectedBatch && !selectedPaymentStatus && !searchTerm && (
              <div className="mt-4 sm:mt-6">
                <Link
                  to="/fees/new"
                  className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                >
                  <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  New Fee Structure
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
          <div className="overflow-x-auto relative">
            {isPageLoading && (
              <div className="absolute inset-0 bg-gray-800/50 flex items-center justify-center z-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Batch
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Total Fee
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Paid
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Outstanding
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {filteredFeeStructures.map((feeStructure) => {
                  const totalPaid = getTotalPaid(feeStructure);
                  const outstanding = feeStructure.final_amount - totalPaid;
                  const paymentStatus = getPaymentStatus(feeStructure);
                  
                  return (
                    <tr key={feeStructure._id || feeStructure.id} className="hover:bg-gray-700">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">
                          {getStudentName(feeStructure.student_id)}
                        </div>
                        {/* Mobile-only batch info */}
                        <div className="sm:hidden text-xs text-gray-400 mt-1">
                          {getBatchName(feeStructure.batch_id)}
                        </div>
                        {/* Mobile-only fee info */}
                        <div className="sm:hidden text-xs text-gray-400 mt-1">
                          Total: {formatIndianCurrency(feeStructure.final_amount)} | Paid: {formatIndianCurrency(totalPaid)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                        <div>{getBatchName(feeStructure.batch_id)}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                        <div>{formatIndianCurrency(feeStructure.final_amount)}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden lg:table-cell">
                        <div>{formatIndianCurrency(totalPaid)}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                        <div>{formatIndianCurrency(Math.max(0, outstanding))}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${paymentStatus.color}`}>
                          {paymentStatus.status}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <Link
                            to={`/fees/${feeStructure._id || feeStructure.id}`}
                            className="p-1 sm:p-2 text-blue-400 hover:text-blue-300 rounded-md hover:bg-gray-700 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </Link>
                          {canSendReminders && outstanding > 0 && (
                            <button
                              onClick={() => handleSendReminder(feeStructure)}
                              className="p-1 sm:p-2 text-yellow-400 hover:text-yellow-300 transition-colors rounded-md hover:bg-gray-700"
                              title="Send Fee Reminder"
                            >
                              <BellIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls - only show when not filtering by student */}
          {!selectedStudent && (
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Page {currentPage + 1} {hasMorePages ? '' : '(last page)'}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0 || isPageLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-md">
                  {currentPage + 1}
                </span>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!hasMorePages || isPageLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </>
        )}
      </div>

      {/* Fee Reminder Form Modal */}
      {showReminderForm && selectedFeeStructure && (
        <FeeReminderForm
          feeStructure={selectedFeeStructure}
          onClose={() => {
            setShowReminderForm(false);
            setSelectedFeeStructure(null);
          }}
          onSuccess={handleReminderSuccess}
        />
      )}
    </div>
  );
};

export default Fees; 